import React from "react";

const LAST_UPDATED = "March 2026";

const Section = ({ title, children }) => (
  <div style={{ marginBottom: 40 }}>
    <h2 style={{ fontFamily: "'Nunito', sans-serif", fontWeight: 800, fontSize: 20, color: "#4A2040", marginBottom: 16, paddingBottom: 10, borderBottom: "1px solid #F0DDE8" }}>{title}</h2>
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>{children}</div>
  </div>
);

const Subsection = ({ title, children }) => (
  <div style={{ marginTop: 8 }}>
    <h3 style={{ fontFamily: "'Nunito', sans-serif", fontWeight: 700, fontSize: 16, color: "#4A2040", marginBottom: 8 }}>{title}</h3>
    {children}
  </div>
);

const TermsOfService = ({ onBack }) => {
  return (
    <div style={{ fontFamily: "'DM Sans', -apple-system, sans-serif", background: "#FDF6F9", minHeight: "100vh", color: "#2D1B28" }}>
      <link href="https://fonts.googleapis.com/css2?family=Nunito:wght@700;800;900&family=DM+Sans:wght@400;500;600&display=swap" rel="stylesheet" />

      {/* Nav */}
      <nav style={{ position: "sticky", top: 0, background: "rgba(253,246,249,0.95)", backdropFilter: "blur(12px)", borderBottom: "1px solid #F0DDE8", padding: "0 5%", height: 64, display: "flex", alignItems: "center", justifyContent: "space-between", zIndex: 10 }}>
        <div style={{ fontFamily: "'Nunito', sans-serif", fontWeight: 900, fontSize: 18, color: "#4A2040", display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 30, height: 30, borderRadius: 8, background: "linear-gradient(135deg, #E8679A, #F48FB1)", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: 900, fontSize: 14 }}>S</div>
          ShrewCare
        </div>
        <button onClick={onBack} style={{ background: "none", border: "1px solid #F0DDE8", borderRadius: 8, padding: "8px 16px", fontSize: 13, fontWeight: 600, color: "#8E6C88", cursor: "pointer", fontFamily: "inherit" }}>← Back</button>
      </nav>

      {/* Content */}
      <div style={{ maxWidth: 760, margin: "0 auto", padding: "56px 5% 100px" }}>
        <div style={{ marginBottom: 48 }}>
          <div style={{ display: "inline-block", fontSize: 12, fontWeight: 800, letterSpacing: "1.5px", textTransform: "uppercase", color: "#E8679A", marginBottom: 12 }}>Legal</div>
          <h1 style={{ fontFamily: "'Nunito', sans-serif", fontWeight: 900, fontSize: "clamp(32px, 5vw, 48px)", color: "#4A2040", marginBottom: 12, lineHeight: 1.1 }}>Terms of Service</h1>
          <p style={{ fontSize: 15, color: "#9E7A90" }}>Last updated: {LAST_UPDATED}</p>
        </div>

        <div style={{ fontSize: 16, lineHeight: 1.8, color: "#3D2A34" }}>
          <Section title="1. Acceptance of Terms">
            <p>These Terms of Service ("Terms") govern your access to and use of the ShrewCare platform and services ("Service") operated by ShrewCare ("we," "us," or "our"). By creating an account or using the Service, you agree to be bound by these Terms.</p>
            <p>If you are using the Service on behalf of a school or organization, you represent that you have authority to bind that organization to these Terms.</p>
          </Section>

          <Section title="2. Description of Service">
            <p>ShrewCare is a school management platform designed for early education and childcare centers. The Service includes tools for attendance tracking, daily activity logging, staff management, parent communication, billing, enrollment management, and related features accessible at shrewcare.com.</p>
          </Section>

          <Section title="3. Account Registration">
            <p>To use the Service, you must create an account by providing accurate and complete information. You are responsible for:</p>
            <ul style={{ paddingLeft: 24, margin: "12px 0" }}>
              {[
                "Maintaining the confidentiality of your account credentials",
                "All activity that occurs under your account",
                "Notifying us immediately of any unauthorized access at support@shrewcare.com",
                "Ensuring that all users you invite to your account comply with these Terms",
              ].map(item => <li key={item} style={{ marginBottom: 8 }}>{item}</li>)}
            </ul>
            <p>You must be at least 18 years old to create an account. Accounts may not be shared between individuals.</p>
          </Section>

          <Section title="4. Subscription and Payment">
            <Subsection title="4.1 Free Trial">
              <p>New accounts receive a 30-day free trial. A valid credit card is required to begin the trial. You will not be charged until the trial period ends. If you cancel before the trial ends, you will not be charged.</p>
            </Subsection>
            <Subsection title="4.2 Subscription Fees">
              <p>After the trial period, the Service is billed at $19 per month per school. Fees are billed in advance on a monthly basis and are non-refundable except as required by law.</p>
            </Subsection>
            <Subsection title="4.3 Payment Processing">
              <p>Payments are processed by Stripe. By providing payment information, you authorize us to charge your payment method for all fees due. You agree to Stripe's Terms of Service at stripe.com/legal.</p>
            </Subsection>
            <Subsection title="4.4 Price Changes">
              <p>We may change our pricing with at least 30 days' notice. Continued use of the Service after a price change constitutes acceptance of the new price.</p>
            </Subsection>
          </Section>

          <Section title="5. Cancellation and Termination">
            <Subsection title="5.1 Cancellation by You">
              <p>You may cancel your subscription at any time through the Settings page in the Service or by contacting us at support@shrewcare.com. Cancellation takes effect at the end of your current billing period. You will retain access to the Service through the end of the paid period.</p>
            </Subsection>
            <Subsection title="5.2 Termination by Us">
              <p>We may suspend or terminate your account at any time if you violate these Terms, fail to pay fees when due, or if we discontinue the Service. We will provide reasonable notice where possible.</p>
            </Subsection>
            <Subsection title="5.3 Effect of Termination">
              <p>Upon termination, your right to use the Service ceases immediately. We will retain your data for 30 days after termination to allow for reactivation, after which it may be permanently deleted.</p>
            </Subsection>
          </Section>

          <Section title="6. Acceptable Use">
            <p>You agree not to use the Service to:</p>
            <ul style={{ paddingLeft: 24, margin: "12px 0" }}>
              {[
                "Violate any applicable laws or regulations",
                "Upload or transmit harmful, illegal, or offensive content",
                "Attempt to gain unauthorized access to the Service or its infrastructure",
                "Interfere with or disrupt the integrity or performance of the Service",
                "Reverse engineer, decompile, or attempt to extract the source code of the Service",
                "Use the Service for any purpose other than managing your school or organization",
                "Share your account credentials with unauthorized individuals",
              ].map(item => <li key={item} style={{ marginBottom: 8 }}>{item}</li>)}
            </ul>
          </Section>

          <Section title="7. Your Data">
            <Subsection title="7.1 Ownership">
              <p>You retain ownership of all data you enter into the Service, including student records, attendance data, and communications. By using the Service, you grant us a limited license to store and process your data solely to provide the Service.</p>
            </Subsection>
            <Subsection title="7.2 Responsibility">
              <p>You are solely responsible for the accuracy, legality, and appropriateness of all data you enter into the Service. You represent that you have all necessary rights and consents to enter student and guardian information into the platform.</p>
            </Subsection>
            <Subsection title="7.3 Data Export">
              <p>You may export your data at any time using the export features within the Service. We are not responsible for data loss if you fail to maintain your own backups.</p>
            </Subsection>
          </Section>

          <Section title="8. Intellectual Property">
            <p>The Service, including its design, code, features, and content, is owned by ShrewCare and protected by intellectual property laws. These Terms do not grant you any right, title, or interest in the Service beyond the limited license to use it as described herein.</p>
            <p>Any feedback, suggestions, or ideas you provide about the Service may be used by us without obligation to you.</p>
          </Section>

          <Section title="9. Privacy">
            <p>Your use of the Service is governed by our Privacy Policy, which is incorporated into these Terms by reference. Please review our Privacy Policy at <a href="https://shrewcare.com" style={{ color: "#E8679A" }}>shrewcare.com</a>.</p>
          </Section>

          <Section title="10. Disclaimers">
            <p>THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. WE DISCLAIM ALL WARRANTIES INCLUDING IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.</p>
            <p>We do not warrant that the Service will be uninterrupted, error-free, or secure. We are not responsible for any loss of data, revenue, or business resulting from use of the Service.</p>
          </Section>

          <Section title="11. Limitation of Liability">
            <p>TO THE MAXIMUM EXTENT PERMITTED BY LAW, SHREWCARE SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOSS OF PROFITS, DATA, OR GOODWILL, ARISING FROM YOUR USE OF OR INABILITY TO USE THE SERVICE.</p>
            <p>OUR TOTAL LIABILITY TO YOU FOR ANY CLAIMS ARISING FROM THESE TERMS OR USE OF THE SERVICE SHALL NOT EXCEED THE AMOUNT YOU PAID TO US IN THE TWELVE MONTHS PRECEDING THE CLAIM.</p>
          </Section>

          <Section title="12. Indemnification">
            <p>You agree to indemnify and hold harmless ShrewCare and its officers, directors, employees, and agents from any claims, damages, losses, or expenses (including legal fees) arising from your use of the Service, your violation of these Terms, or your violation of any rights of a third party.</p>
          </Section>

          <Section title="13. Governing Law">
            <p>These Terms are governed by the laws of the United States. Any disputes arising from these Terms or the Service shall be resolved through binding arbitration in accordance with the rules of the American Arbitration Association, except that either party may seek injunctive relief in a court of competent jurisdiction.</p>
          </Section>

          <Section title="14. Changes to Terms">
            <p>We may update these Terms from time to time. We will notify you of material changes by email or by posting a notice in the Service at least 14 days before changes take effect. Your continued use of the Service after changes take effect constitutes acceptance of the revised Terms.</p>
          </Section>

          <Section title="15. Contact Us">
            <p>If you have questions about these Terms, please contact us at:</p>
            <div style={{ marginTop: 16, padding: "20px 24px", background: "white", borderRadius: 12, border: "1px solid #F0DDE8" }}>
              <p style={{ margin: 0, fontWeight: 600 }}>ShrewCare</p>
              <p style={{ margin: "4px 0 0" }}>Email: <a href="mailto:support@shrewcare.com" style={{ color: "#E8679A" }}>support@shrewcare.com</a></p>
              <p style={{ margin: "4px 0 0" }}>Website: <a href="https://shrewcare.com" style={{ color: "#E8679A" }}>shrewcare.com</a></p>
            </div>
          </Section>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
